import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  ClientTag,
  TAG_COLORS,
  fetchTags,
  createTag,
  updateTag,
  deleteTag,
} from '../services/api';

const DEFAULT_COLOR = TAG_COLORS[0];

export default function ManageTagsTab() {
  const [tags, setTags] = useState<ClientTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Create / edit dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ClientTag | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [colorInput, setColorInput] = useState<string>(DEFAULT_COLOR);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  // Delete confirmation (requires typing the tag name)
  const [deleteTarget, setDeleteTarget] = useState<ClientTag | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTags(await fetchTags());
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Failed to load tags', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setNameInput('');
    setColorInput(DEFAULT_COLOR);
    setEditorError(null);
    setEditorOpen(true);
  };

  const openEdit = (tag: ClientTag) => {
    setEditing(tag);
    setNameInput(tag.Name);
    setColorInput(tag.Color || DEFAULT_COLOR);
    setEditorError(null);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    const name = nameInput.trim();
    if (!name) return;
    setSaving(true);
    setEditorError(null);
    try {
      if (editing) {
        const updated = await updateTag(editing.Id, { Name: name, Color: colorInput });
        setTags((prev) => prev
          .map((t) => (t.Id === editing.Id ? { ...t, Name: updated.Name, Color: updated.Color } : t))
          .sort((a, b) => a.Name.localeCompare(b.Name)));
      } else {
        const created = await createTag({ Name: name, Color: colorInput });
        setTags((prev) => [...prev, created].sort((a, b) => a.Name.localeCompare(b.Name)));
      }
      setEditorOpen(false);
    } catch (e) {
      setEditorError(e instanceof Error ? e.message : 'Failed to save tag');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (tag: ClientTag) => {
    setDeleteTarget(tag);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteTag(deleteTarget.Id);
      setTags((prev) => prev.filter((t) => t.Id !== deleteTarget.Id));
      setDeleteTarget(null);
      setToast({ msg: 'Tag deleted', severity: 'success' });
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete tag');
    } finally {
      setDeleteBusy(false);
    }
  };

  const deleteConfirmed = !!deleteTarget && deleteConfirmText.trim() === deleteTarget.Name;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
          Create tags to label and group your clients. Filter by tag on the Clients and Pipeline pages.
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={openCreate}
          sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
        >
          New Tag
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : tags.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <LocalOfferIcon sx={{ fontSize: 40, color: 'var(--color-text-faint)', mb: 1 }} />
          <Typography sx={{ color: 'var(--color-text-faint)', mb: 2 }}>No tags yet</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
            Create your first tag
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {tags.map((tag) => (
            <Card key={tag.Id} sx={{ bgcolor: 'var(--color-surface)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Chip
                  label={tag.Name}
                  size="small"
                  sx={{
                    bgcolor: `${tag.Color || DEFAULT_COLOR}22`,
                    color: tag.Color || DEFAULT_COLOR,
                    border: `1px solid ${tag.Color || DEFAULT_COLOR}55`,
                    fontWeight: 700,
                  }}
                />
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                  {tag.ClientCount ?? 0} client{(tag.ClientCount ?? 0) === 1 ? '' : 's'}
                </Typography>
                <Tooltip title="Edit tag">
                  <IconButton size="small" onClick={() => openEdit(tag)} sx={{ color: 'var(--color-text-subtle)' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete tag">
                  <IconButton size="small" onClick={() => openDelete(tag)} sx={{ color: 'var(--color-text-faint)', '&:hover': { color: '#ef4444' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={editorOpen}
        onClose={() => !saving && setEditorOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>{editing ? 'Edit Tag' : 'New Tag'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Tag name"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && nameInput.trim()) handleSave(); }}
            inputProps={{ maxLength: 100 }}
            sx={{ mt: 1, mb: 2 }}
            placeholder="e.g. Hot Lead, VIP, Follow-up"
          />
          <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>Colour</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {TAG_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => setColorInput(c)}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: c,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: colorInput === c ? '2px solid var(--color-text-strong)' : '2px solid transparent',
                  transition: 'transform 0.1s',
                  '&:hover': { transform: 'scale(1.1)' },
                }}
              >
                {colorInput === c && <CheckIcon sx={{ fontSize: 18, color: '#fff' }} />}
              </Box>
            ))}
          </Box>
          {/* Live preview */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 0.5 }}>Preview</Typography>
            <Chip
              label={nameInput.trim() || 'Tag name'}
              size="small"
              sx={{
                bgcolor: `${colorInput}22`,
                color: colorInput,
                border: `1px solid ${colorInput}55`,
                fontWeight: 700,
              }}
            />
          </Box>
          {editorError && <Alert severity="error" sx={{ mt: 2 }}>{editorError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditorOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !nameInput.trim()}
            onClick={handleSave}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {saving ? 'Saving...' : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation — type the tag name to confirm */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>Delete tag?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 2 }}>
            This removes the tag <strong>{deleteTarget?.Name}</strong> from{' '}
            {deleteTarget?.ClientCount ?? 0} client{(deleteTarget?.ClientCount ?? 0) === 1 ? '' : 's'} and cannot be undone.
            Type <strong>{deleteTarget?.Name}</strong> to confirm.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && deleteConfirmed) handleDelete(); }}
            placeholder={deleteTarget?.Name}
            autoFocus
          />
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteBusy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={deleteBusy || !deleteConfirmed}
            onClick={handleDelete}
            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none' }}
          >
            {deleteBusy ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
